import { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useSidebar } from '@/components/ui/sidebar';
import { NAV_RAIL_WIDTH, useNavModeStore } from '@/stores/nav-mode-store';
import { ChevronDown, Pin, PinOff, Plus, Tag, X } from 'lucide-react';
import type { Label } from '@/types/workflow-types';

/**
 * Sidebar widths — mirror the constants inside `@/components/ui/sidebar.tsx`.
 * Kept local so we don't import private values from that module.
 */

interface LabelFilterBandProps {
  allLabels: Label[];
  selectedLabelIds: string[];
  onToggleLabel: (labelId: string) => void;
  onClear: () => void;
  isLoading?: boolean;
}

export function LabelFilterBand({
  allLabels,
  selectedLabelIds,
  onToggleLabel,
  onClear,
  isLoading = false,
}: LabelFilterBandProps) {
  const [open, setOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isPinned, setIsPinned] = useState(false);

  const bandRef = useRef<HTMLDivElement>(null);

  // Anchor the expanded band to the dashboard content area (right of the
  // sidebar) rather than the viewport center, so it doesn't look lopsided.
  const { isMobile } = useSidebar();
  const navMode = useNavModeStore((state) => state.mode);
  // The rail overlays the page rather than widening it, so the reserved space
  // is always the icon rail.
  const sidebarOffset = isMobile ? '0px' : NAV_RAIL_WIDTH[navMode];

  // Briefly highlight the minimized pill when the selection changes while
  // minimized, so drill-down clicks still give visual feedback.
  const [justUpdated, setJustUpdated] = useState(false);
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!isMinimized) return;
    setJustUpdated(true);
    const t = setTimeout(() => setJustUpdated(false), 900);
    return () => clearTimeout(t);
  }, [selectedLabelIds, isMinimized]);

  // Auto-minimize when the user clicks outside, unless pinned or a popover is open.
  useEffect(() => {
    if (isMinimized) return;
    function handleOutsideClick(e: MouseEvent) {
      if (isPinned || open) return;
      if (bandRef.current && !bandRef.current.contains(e.target as Node)) {
        setIsMinimized(true);
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isMinimized, isPinned, open]);

  const selectedSet = useMemo(
    () => new Set(selectedLabelIds),
    [selectedLabelIds],
  );

  const selectedLabels = useMemo(
    () => allLabels.filter((l) => selectedSet.has(l.id)),
    [allLabels, selectedSet],
  );

  const hasSelection = selectedLabels.length > 0;

  if (isMinimized) {
    return (
      <div className='fixed bottom-4 right-4 z-40'>
        <button
          type='button'
          onClick={() => setIsMinimized(false)}
          className={[
            'group inline-flex items-center gap-2 rounded-full border border-sky-400/70 bg-background px-3 py-2 text-sm shadow-lg transition-all hover:shadow-xl',
            justUpdated ? 'ring-2 ring-sky-400/60 ring-offset-2' : '',
          ].join(' ')}
          title='Expand label filter'
        >
          <Tag className='size-4 text-sky-600' />
          <span className='font-medium'>Labels</span>
          {hasSelection ? (
            <span className='inline-flex min-w-5 items-center justify-center rounded-full bg-sky-600 px-1.5 text-[11px] font-semibold text-white'>
              {selectedLabels.length}
            </span>
          ) : (
            <span className='text-xs text-muted-foreground'>
              filter by label
            </span>
          )}
          <ChevronDown className='size-3.5 rotate-180 text-muted-foreground transition-transform group-hover:text-foreground' />
        </button>
      </div>
    );
  }

  return (
    <div
      className='pointer-events-none fixed bottom-4 right-0 z-40 flex justify-center transition-[left] duration-200 ease-linear'
      style={{ left: sidebarOffset }}
    >
      <div
        ref={bandRef}
        className='pointer-events-auto w-[min(760px,calc(100%-2rem))] rounded-xl border border-sky-400/70 bg-background/95 shadow-2xl backdrop-blur supports-backdrop-filter:bg-background/80'
      >
        {/* Header row */}
        <div className='flex items-center justify-between border-b border-border/60 px-4 py-2'>
          <div className='flex items-center gap-2 text-sm'>
            <Tag className='size-4 text-sky-600' />
            <span className='font-medium'>Filter by labels</span>
          </div>
          <button
            type='button'
            onClick={() => setIsPinned((p) => !p)}
            className={[
              'inline-flex size-7 items-center justify-center rounded-md transition-colors',
              isPinned
                ? 'bg-sky-100 text-sky-600 hover:bg-sky-200 dark:bg-sky-950 dark:text-sky-400'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            ].join(' ')}
            title={
              isPinned
                ? 'Unpin — band will close on outside click'
                : 'Pin open — prevent auto-close'
            }
          >
            {isPinned ? (
              <PinOff className='size-4' />
            ) : (
              <Pin className='size-4' />
            )}
          </button>
        </div>

        {/* Body — selection + controls */}
        <div className='flex flex-wrap items-center gap-2 px-4 py-3'>
          {hasSelection ? (
            selectedLabels.map((label) => (
              <button
                key={label.id}
                type='button'
                onClick={() => onToggleLabel(label.id)}
                className='group inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity hover:opacity-80'
                style={{
                  borderColor: label.color || '#9CA3AF',
                  backgroundColor: `${label.color || '#9CA3AF'}24`,
                }}
                title={`Remove "${label.name}"`}
              >
                <span
                  className='size-2 rounded-full'
                  style={{ backgroundColor: label.color || '#9CA3AF' }}
                />
                {label.name}
                <X className='size-3 text-muted-foreground group-hover:text-foreground' />
              </button>
            ))
          ) : (
            <span className='text-xs text-muted-foreground'>
              No labels selected — showing all workflows
            </span>
          )}

          <div className='ml-auto flex items-center gap-2'>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-7 gap-1 px-2 text-xs'
                  disabled={isLoading || allLabels.length === 0}
                >
                  <Plus className='size-3' />
                  {hasSelection ? 'Add' : 'Select labels'}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align='end'
                side='top'
                className='w-72 p-0'
                sideOffset={8}
              >
                <div className='px-3 py-2 text-xs font-medium text-muted-foreground'>
                  Workflow labels
                </div>
                <div className='max-h-64 overflow-auto'>
                  {allLabels.length === 0 ? (
                    <div className='px-3 py-4 text-xs text-muted-foreground'>
                      No labels defined for this org.
                    </div>
                  ) : (
                    allLabels.map((label) => {
                      const isSelected = selectedSet.has(label.id);
                      return (
                        <button
                          key={label.id}
                          type='button'
                          onClick={() => onToggleLabel(label.id)}
                          className='flex w-full items-center gap-2.5 border-t border-border/50 px-3 py-2 text-sm hover:bg-accent'
                        >
                          <span className='flex size-4 shrink-0 items-center justify-center'>
                            {isSelected && (
                              <svg
                                className='size-3.5 text-primary'
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth='3'
                                strokeLinecap='round'
                                strokeLinejoin='round'
                              >
                                <polyline points='20 6 9 17 4 12' />
                              </svg>
                            )}
                          </span>
                          <span
                            className='size-3.5 shrink-0 rounded-full'
                            style={{
                              backgroundColor: label.color || '#6B7280',
                            }}
                          />
                          <span className='truncate text-left font-medium'>
                            {label.name}
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {hasSelection && (
              <Button
                variant='ghost'
                size='sm'
                className='h-7 px-2 text-xs text-muted-foreground'
                onClick={onClear}
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
